/**
 * SPA data client — SPA architectural convergence plan §6.1.
 *
 * This file replaces the Phase-0 polling client. The legacy
 * implementation polled the upstream Zenoh REST plugin directly under
 * the `rmf/sites/{site}/...` keyexprs with no auth, no manifest,
 * no tombstone, no envelope. The convergence-target client uses:
 *
 *   - the global `nexsoss-api-server` REST surface (auth, sites,
 *     capabilities, commands, history, audit);
 *   - canonical v1 models from `./v1`;
 *   - the fetch-SSE live client in `./fetch-sse`;
 *   - the per-category reducers in `./category-reducers`.
 *
 * The exported function names below (fetchFleets, fetchRobots,
 * fetchTasks, fetchAlerts, fetchDoors, fetchLifts, fetchBuildingMap)
 * match the legacy names so existing screens keep working. Each one
 * now goes through the v1 API client (for initial loads) and the
 * fetch-SSE transport (for live updates), both of which enforce the
 * v1 envelope + manifest + tombstone contract.
 */
import { plainLaneRefusal } from '../state/lift-rule-waiting';
import {
  ApiClientError,
  ApiNetworkError,
  acquireLiftSession,
  commissionRobot,
  controlTask,
  dispatchTask,
  doorCommand,
  fetchAssignedSites,
  fetchAudit,
  fetchBuildingMap,
  fetchLiftsRegistry,
  fetchReady,
  fetchSiteCapabilities,
  fetchTaskLog,
  liftCommand,
  releaseLiftSession,
} from './v1-client';
import {
  fetchDoorsSnapshot,
  fetchFleetsSnapshot,
  fetchLiftsSnapshot,
  fetchRobotsSnapshot,
  fetchTasksSnapshot,
} from './v1-zenoh-client';
import type {
  AuditEntry,
  BuildingMapSnapshot,
  CapabilityDisabledReason,
  CommandRecord,
  DoorState as V1DoorState,
  FleetRegistry,
  LiftRegistry,
  LiftSession,
  LiftState as V1LiftState,
  RobotState as V1RobotState,
  SiteCapabilities,
  SiteId,
  SiteSummary,
  TaskLogEntry,
  TaskPhase,
  TaskSummary as V1TaskSummary,
} from './v1';
import type { DoorViewModel, LiftViewModel, DoorLastCommandView } from './adapters';
import { PRIORITY_HIGH, isHighPriority } from './task-priority';

// ----------------------------------------------------------------------------
// SPA-facing view-model types (legacy names + v1-backed shapes).
//
// These intentionally mirror the legacy Phase-0 shapes so the
// existing screens (Fleet, Devices, Tasks, Map) keep compiling
// without an immediate rewrite. The screen-level migrations will
// follow incrementally and switch to the canonical v1 types.
// ----------------------------------------------------------------------------

export type RobotStatusView =
  | 'idle'
  | 'charging'
  | 'active'
  | 'error'
  | 'offline'
  /** Configured and reachable, but RMF cannot place it on the nav graph:
   *  it needs re-localizing. NOT offline -- the robot is powered and one
   *  `initialize` call from working, and sending someone to check cables
   *  is the wrong action. */
  | 'pending-localize';

export interface RobotStateView {
  name: string;
  fleet: string;
  status: RobotStatusView;
  battery?: number;
  location?: { map: string; x: number; y: number; yaw?: number };
  lastSeen?: string;
  siteId?: string;
  vendor?: string;
  /** Current task id, if any — lets the robot panel show what the
   *  robot is doing and offer the same task controls as the Tasks
   *  screen without a separate lookup round-trip. */
  taskId?: string;
  /**
   * The worker's `unavailable_reason`, carried through as reported
   * (`not_localized`, `edge_offline`, `state_channel_stale`, ...).
   * Absent for a healthy robot. `status` alone cannot say it: a robot
   * the adapter cannot place and one nobody can reach both arrive with
   * a placeholder pose, and only this says which.
   */
  unavailableReason?: string;
  /**
   * The worker's `updated_at_ms`, as a number, when it is a real
   * instant; undefined otherwise. It is the WORKER's clock: compare it
   * only with another stamp from the same record stream (is this record
   * newer than the last one?), never with this device's `Date.now()`.
   * How long ago a robot was heard from is a device-clock receipt age
   * (`robot-receipts.ts`).
   */
  updatedAtMs?: number;
}

export interface FleetSummaryView {
  fleets: { name: string; robots: number }[];
}

/**
 * Legacy API health shape (Phase-0). The v1 client surfaces
 * `ReadyzResponse` (component-level readiness, plan §3.7); the
 * legacy shape is preserved here for callers that haven't migrated.
 */
export interface ApiHealth {
  status: 'ready' | 'degraded' | 'unreachable';
  components?: Record<string, { status: string; detail?: string }>;
}

// Legacy Phase-0 aliases (convergence plan §6.1: screens consume
// these names during the incremental migration). New code MUST use
// the canonical v1 types from `./v1`.
//
// IMPORTANT: the canonical-name aliases (`RobotState`/`TaskState`/
// `AlertState`) remain here as back-compat shims for the ~20
// screens that still import them by their short names. The new
// `Legacy*` aliases are the documented path for new code and
// will become the only exports once the per-screen refactor lands.
export type LegacyAlertState = AlertStateView;
export type LegacyRobotState = RobotStateView;
export type LegacyTaskState = TaskStateView;
export type AlertState = AlertStateView;
export type RobotState = RobotStateView;
export type TaskState = TaskStateView;

export interface TaskStateView {
  id: string;
  type: string;
  assignedRobot?: string;
  /** `TaskSummary.assigned_fleet`. With `assignedRobot`, the `<fleet>/<robot>`
   *  key the calendar's robot lanes file a task under. */
  assignedFleet?: string;
  status: 'queued' | 'active' | 'completed' | 'failed' | 'cancelled' | 'interrupted';
  triggeredBy?: string;
  startedAt?: string;
  durationSec?: number;
  siteId?: string;
  /** Ordered "waypoints visited" record — see `TaskSummary.phases`. */
  phases?: TaskPhase[];
  /** RMF's own live remaining-time estimate, seconds — see
   *  `TaskSummary.estimated_remaining_sec`. */
  estimatedRemainingSec?: number;
  /** RMF's estimate of the task's WHOLE run, seconds -- see
   *  `TaskSummary.estimated_duration_sec`. What a queued task's
   *  "Est. time remaining" shows, since it has no remaining estimate
   *  of its own until it starts. */
  estimatedDurationSec?: number;
  /** Epoch ms the task was submitted. Kept (it used to be dropped)
   *  because it is the ONLY timestamp a never-started task carries,
   *  and the quiet-task badge has nothing else to measure from — see
   *  `task-staleness.ts`. */
  submittedAtMs?: number;
  /** Epoch ms the task reached a terminal state. */
  completedAtMs?: number;
  /** `TaskSummary.failure_reason`. */
  failureReason?: string;
  /** `TaskSummary.synthetic` — the platform closed this record out;
   *  no robot failed. */
  synthetic?: boolean;
  /** RMF's own raw status (`TaskSummary.rmf_status`). Display and log
   *  only: no control decision branches on it. Absent from a 0.4.28
   *  worker and under its `NEXSOSS_TASK_ERROR_IS_TERMINAL` lever. */
  rmfStatus?: string;
  /**
   * "Still running, and a human should look at it."
   *
   * RMF's `error` is not a terminal — the fleet adapter re-enters it
   * every 5 s while it retries a plan. Worker 0.4.28 mapped it to
   * `failed`, which is why Cancel and Kill disappeared for 47 minutes
   * on 2026-09-15 while a robot sat wedged at a disarmed lift.
   */
  attention?: { rmfStatus: string; sinceMs: number; reason?: string };
  /** When RMF began saying it can find this task no way to its next leg
   *  (`TaskSummary.no_route_since_ms`). It names no lift and has other causes:
   *  `state/lift-rule-waiting.ts` says what it means. */
  noRouteSinceMs?: number;
  /** Which structural signal ENDED this task, when the platform is the
   *  one saying it ended (`TaskSummary.settled_by`). */
  settledBy?: 'rmf_released' | 'dispatcher_no_bid' | 'clean_failure_marker';
  /** 1 for a High task (`TaskSummary.priority`, read through
   *  `priorityLevel`); absent for Normal, and from a worker before 0.4.40,
   *  which never sends priority to the robots. */
  priority?: typeof PRIORITY_HIGH;
  /** Epoch ms the platform last published anything about this task —
   *  the envelope's `projector_received_at_ms`, NOT a field of the
   *  task itself. This is what the quiet-task badge measures from;
   *  see `screens/Tasks/task-staleness.ts`. Undefined only when the
   *  record reached us by a path that carried no envelope. */
  lastUpdateMs?: number;
}

export interface AlertStateView {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  source: string;
  message: string;
  timestamp: string;
  status: 'open' | 'acknowledged' | 'resolved';
  siteId?: string;
}

export interface DoorStateView {
  id: string;
  level: string;
  mode: 'open' | 'closed' | 'moving' | 'unknown';
  v1X?: number;
  v1Y?: number;
  v1Yaw?: number;
  doorWidthM?: number;
}

export interface LiftStateView {
  id: string;
  level: string;
  currentLevel: string;
  destinationLevel?: string | null;
  mode: 'agv' | 'human' | 'off' | 'fire' | 'unknown';
  doorState: 'open' | 'closed' | 'moving' | 'unknown';
  motionState: 'stopped' | 'moving' | 'unknown';
  fault: boolean;
  v1X?: number;
  v1Y?: number;
  v1Yaw?: number;
}

export interface TaskCreateRequest {
  type: string;
  payload: Record<string, unknown>;
  priority?: number;
  notes?: string;
  siteId?: string;
}

export interface CommandOutcomeView {
  command_id: string;
  state: CommandRecord['state'];
  outcome_message?: string | undefined;
}

// ----------------------------------------------------------------------------
// Sites + readiness (replaces useSites Zenoh discovery, plan §6.3)
// ----------------------------------------------------------------------------

export async function fetchSites(): Promise<SiteSummary[]> {
  return fetchAssignedSites();
}

export async function fetchCapabilities(site: SiteId): Promise<SiteCapabilities> {
  return fetchSiteCapabilities(site);
}

export async function fetchHealth(): Promise<{ status: 'ready' | 'degraded' | 'unreachable'; components?: Record<string, { status: string; detail?: string }> }> {
  try {
    const r = await fetchReady();
    return {
      status: r.status === 'healthy' ? 'ready' : r.status === 'degraded' ? 'degraded' : 'unreachable',
      components: Object.fromEntries(
        Object.entries(r.components).map(([k, v]) => [k, { status: v.status, detail: v.detail ?? undefined }]),
      ),
    };
  } catch {
    return { status: 'unreachable' };
  }
}

// ----------------------------------------------------------------------------
// Fleet / robots (initial-load reads; live updates flow through the
// fetch-SSE transport and the per-category reducer)
// ----------------------------------------------------------------------------

/**
 * v1 → view-model adapters. Exported so `useLiveData`'s SSE overlay
 * applies the exact same mapping as the REST seed above — the
 * architecture forbids the REST and stream paths ever writing
 * divergent representations of the same logical entity (plan §6.2).
 */
export function adaptFleetRegistry(registries: FleetRegistry[]): FleetSummaryView {
  return { fleets: registries.map((f) => ({ name: f.id.fleet, robots: f.robots.length })) };
}

export async function fetchFleets(siteId: string): Promise<FleetSummaryView> {
  const registries = await fetchFleetsSnapshot(siteId as SiteId);
  return adaptFleetRegistry(registries);
}

/** ECMAScript's largest representable time value (±8.64e15 ms). */
const MAX_EPOCH_MS = 8.64e15;

/** `ms` when it is an instant `new Date()` can hold, else undefined.
 *  The range test alone also refuses NaN and both infinities. */
function realEpochMs(ms: unknown): number | undefined {
  return typeof ms === 'number' && ms > 0 && ms <= MAX_EPOCH_MS ? ms : undefined;
}

export function adaptRobotState(r: V1RobotState): RobotStateView {
  // Never `new Date(r.updated_at_ms).toISOString()` unguarded: on a
  // NaN, infinite or absent stamp that throws a RangeError, and this
  // runs inside the live flush for every robot at once. A record with
  // no usable stamp is still a record; it just cannot say when.
  const updatedAtMs = realEpochMs(r.updated_at_ms);
  const reason = typeof r.unavailable_reason === 'string' ? r.unavailable_reason : '';
  return {
    name: r.id.robot,
    fleet: r.id.fleet,
    status: r.status,
    battery: r.battery_percent,
    location: r.location
      ? { map: r.location.map, x: r.location.x, y: r.location.y, yaw: r.location.yaw }
      : undefined,
    lastSeen: updatedAtMs !== undefined ? new Date(updatedAtMs).toISOString() : undefined,
    siteId: r.id.site,
    taskId: r.task_id?.task,
    unavailableReason: reason || undefined,
    updatedAtMs,
  };
}

export async function fetchRobots(siteId: string): Promise<RobotStateView[]> {
  const robots = await fetchRobotsSnapshot(siteId as SiteId);
  return robots.map(adaptRobotState);
}

// ----------------------------------------------------------------------------
// Tasks (initial load + command lifecycle)
// ----------------------------------------------------------------------------

export function adaptTaskSummary(t: V1TaskSummary, lastUpdateMs?: number): TaskStateView {
  // `!= null` (loose) on purpose, not `!== undefined` (strict) — the
  // backend's `Optional[int] = None` fields serialize as a JSON
  // `null` that IS present on the wire, not an omitted key, so
  // `t.started_at_ms` arrives as JS `null`, not `undefined`. The
  // strict check this used to be treated `null !== undefined` as
  // "value present", so `Math.round((null - null) / 1000)` (both
  // operands coerce to 0) silently computed `durationSec: 0` for
  // every active task that hadn't started/finished yet — confirmed
  // live: the Tasks screen showed "0s" instead of falling through to
  // the estimate/elapsed-time display.
  const startedAtMs = t.started_at_ms ?? undefined;
  const completedAtMs = t.completed_at_ms ?? undefined;
  return {
    id: t.id.task,
    type: t.category,
    assignedRobot: t.assigned_robot,
    assignedFleet: t.assigned_fleet || undefined,
    status: t.state,
    triggeredBy: t.triggered_by,
    startedAt: startedAtMs !== undefined ? new Date(startedAtMs).toISOString() : undefined,
    durationSec:
      completedAtMs !== undefined && startedAtMs !== undefined
        ? Math.round((completedAtMs - startedAtMs) / 1000)
        : undefined,
    siteId: t.id.site,
    phases: t.phases,
    estimatedRemainingSec: t.estimated_remaining_sec ?? undefined,
    estimatedDurationSec: t.estimated_duration_sec ?? undefined,
    submittedAtMs: t.submitted_at_ms ?? undefined,
    completedAtMs,
    // The one failure written for a log that must reach staff: a task refused
    // while a lift is blocked. Every surface reads this field, so it says it
    // in plain words once, here.
    failureReason: plainLaneRefusal(t.failure_reason) ?? t.failure_reason,
    synthetic: t.synthetic === true,
    rmfStatus: t.rmf_status ?? undefined,
    attention: t.attention
      ? {
          rmfStatus: t.attention.rmf_status,
          sinceMs: t.attention.since_ms,
          reason: t.attention.reason ?? undefined,
        }
      : undefined,
    noRouteSinceMs: typeof t.no_route_since_ms === 'number' ? t.no_route_since_ms : undefined,
    settledBy: t.settled_by ?? undefined,
    priority: isHighPriority(t.priority) ? PRIORITY_HIGH : undefined,
    lastUpdateMs: Number.isFinite(lastUpdateMs) ? lastUpdateMs : undefined,
  };
}

export async function fetchTasks(siteId: string): Promise<TaskStateView[]> {
  const tasks = await fetchTasksSnapshot(siteId as SiteId);
  return tasks.map((e) => adaptTaskSummary(e.task, e.receivedAtMs));
}

export async function fetchTaskLogEntries(site: SiteId, taskId: string): Promise<TaskLogEntry[]> {
  const r = await fetchTaskLog(site, { site, task: taskId });
  return r.entries;
}

// ----------------------------------------------------------------------------
// Alerts (initial load; live updates from the SSE stream's
// `rmf/v1/sites/{site}/alerts` events)
// ----------------------------------------------------------------------------

export async function fetchAlerts(_siteId: string): Promise<AlertStateView[]> {
  return [];
}

// ----------------------------------------------------------------------------
// Doors + lifts (initial-load read-through; live updates from SSE)
// ----------------------------------------------------------------------------

/**
 * Adapt a live-state-only v1 `DoorState` into the screens' existing
 * `DoorViewModel` shape. Geometry (`level`, `v1`/`v2` line-segment
 * endpoints, `doorType`) is not carried by the state-only category —
 * it lives in the (not-yet-fetched) `DoorRegistry` — so those fields
 * are left unset here rather than guessed. The map-parity pass adds
 * a registry fetcher and merges geometry back in.
 */
function adaptV1DoorLastCommand(c: V1DoorState['last_command']): DoorLastCommandView | undefined {
  // Validated field by field rather than passed through: this is
  // rendered as a sentence about a named person or robot, and a
  // malformed record must read as "no command", never as "opened by
  // undefined".
  if (!c || typeof c !== 'object') return undefined;
  const source = c.source === 'console' || c.source === 'robot' || c.source === 'other' ? c.source : undefined;
  const requested = c.requested === 'open' || c.requested === 'close' ? c.requested : undefined;
  if (!source || !requested) return undefined;
  if (typeof c.actor !== 'string' || c.actor.trim() === '') return undefined;
  // A real instant, not merely a finite number: `new Date(1e300)` is
  // Invalid Date and `toISOString()` on it throws in render.
  if (typeof c.at_ms !== 'number' || !Number.isFinite(c.at_ms)) return undefined;
  if (c.at_ms <= 0 || c.at_ms > MAX_EPOCH_MS) return undefined;
  return { source, actor: c.actor, requested, atMs: c.at_ms };
}

export function adaptV1DoorState(d: V1DoorState): DoorViewModel {
  return {
    id: d.id.door,
    name: d.id.door,
    level: '',
    state: d.mode,
    lastSeen: new Date(d.updated_at_ms).toISOString(),
    siteId: d.id.site,
    lastCommand: adaptV1DoorLastCommand(d.last_command),
    // A published `DoorState` is the definition of live: something on
    // the edge wrote this key. The registry-only rows that
    // `mergeDoorsWithRegistry` appends are the ones that carry false.
    hasLiveState: true,
  };
}

export async function fetchDoors(siteId: string): Promise<DoorViewModel[]> {
  const doors = await fetchDoorsSnapshot(siteId as SiteId);
  return doors.map(adaptV1DoorState);
}

/**
 * `levels`/`ref`/`width`/`depth` come from the `LiftRegistry` (static
 * geometry), not the live `LiftState` — merged in by `fetchLifts`
 * below. Without `levels`, `LiftDetailDrawer`'s destination-floor
 * dropdown had nothing to populate (`lift.levels` was always `[]`),
 * even though `doors.py`/`lifts.py`'s registry read-through has
 * served real data since Phase 4B.
 */
export function adaptV1LiftState(l: V1LiftState, registry?: LiftRegistry): LiftViewModel {
  return {
    id: l.id.lift,
    name: l.id.lift,
    levels: registry?.levels ?? [],
    currentLevel: l.current_level,
    destinationLevel: l.destination_level ?? undefined,
    motionState: l.fault ? 'fault' : l.motion_state === 'stopped' ? 'idle' : l.motion_state,
    motionDirection: l.motion_direction ?? undefined,
    // Both halves, not just the collapsed boolean. `fault` alone
    // cannot say WHY -- MODE_OFFLINE(4) on a disarmed bridge and a
    // genuinely dead controller set the identical flag -- and `mode`
    // alone cannot tell MODE_EMERGENCY(5) from MODE_UNKNOWN(0),
    // because the v1 mapping collapses both to 'unknown'. The pair
    // does; see `lift-dispatch-status.ts`, which is the only place
    // that interprets them.
    mode: l.mode,
    fault: l.fault,
    doorState: l.door_state,
    rmfSessionId: l.rmf_session_id || undefined,
    // Undefined until the projection carries it. The Release
    // button stays disabled rather than sending a phase the SPA
    // invented. `session.phase` is the same string from worker 0.4.8
    // on and wins; `session_phase` is what a 0.4.7 worker published.
    sessionPhase: (l.session?.phase ?? l.session_phase) || undefined,
    // `null` (adapter not yet heard from) and absent (0.4.7 worker)
    // both become undefined: neither is a session, and the drawer
    // must offer Request Floor only in both cases.
    session: l.session ?? undefined,
    standingRequest: l.standing_request ?? undefined,
    ref: registry ? { x: registry.v1_x, y: registry.v1_y } : undefined,
    refYaw: registry?.v1_yaw,
    width: registry?.door_width_m,
    depth: registry?.door_height_m,
    lastSeen: new Date(l.updated_at_ms).toISOString(),
    siteId: l.id.site,
    // See `adaptV1DoorState`: reaching this function means the car
    // published a state.
    hasLiveState: true,
  };
}

export async function fetchLifts(siteId: string): Promise<LiftViewModel[]> {
  const [lifts, registries] = await Promise.all([
    fetchLiftsSnapshot(siteId as SiteId),
    fetchLiftsRegistry(siteId as SiteId).catch(() => [] as LiftRegistry[]),
  ]);
  const registryByLift = new Map(registries.map((r) => [r.id.lift, r]));
  return lifts.map((l) => adaptV1LiftState(l, registryByLift.get(l.id.lift)));
}

// ----------------------------------------------------------------------------
// Building map (read-through to RocksDB-cached v1 snapshot)
// ----------------------------------------------------------------------------

export async function fetchBuildingMapSnapshot(
  site: SiteId,
  level = 'L1',
  revision?: string,
): Promise<BuildingMapSnapshot | null> {
  return fetchBuildingMap(site, level, revision);
}

// ----------------------------------------------------------------------------
// Commands (plan §Command lifecycle)
// ----------------------------------------------------------------------------

export async function createTask(
  _req: TaskCreateRequest,
): Promise<TaskStateView> {
  // Phase-0 write path stub removed. Real dispatch goes through
  // `dispatchTask()` which the screens now call directly. This
  // function remains so legacy code that referenced it compiles;
  // it now throws a clean error so callers fail loud rather than
  // silently dropping a write to a backend that doesn't accept it.
  throw new Error(
    'createTask is a Phase-0 stub. Use dispatchTask() with the v1 contract instead.',
  );
}

export async function updateAlert(_id: string, _patch: Partial<AlertStateView>): Promise<AlertStateView> {
  throw new Error(
    'updateAlert is a Phase-0 stub. Use the v1 admin endpoints instead.',
  );
}

// Re-export the v1 command helpers so screens can import them from
// the same path as before.
export {
  acquireLiftSession,
  commissionRobot,
  controlTask,
  dispatchTask,
  doorCommand,
  fetchAssignedSites,
  fetchAudit,
  fetchBuildingMap,
  fetchReady,
  fetchSiteCapabilities,
  fetchTaskLog,
  liftCommand,
  releaseLiftSession,
};

export type {
  AuditEntry,
  BuildingMapSnapshot,
  CapabilityDisabledReason,
  CommandRecord,
  V1DoorState as DoorState,
  FleetRegistry,
  LiftSession,
  V1LiftState as LiftState,
  SiteCapabilities,
  SiteId,
  SiteSummary,
  TaskLogEntry,
};

export { ApiClientError, ApiNetworkError };